import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef, NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { User, UserTab } from "../../../auth/models";
import { Company } from '../../../auth/models/company';
import { ConfirmModalComponent } from '../../../common/components/confirm-modal/confirm-modal.component';
import { ToastService } from '../../../main/services';
import { Supplier } from '../../models/supplier/supplier';
import { SuppliersService } from '../../services';
import { CreateSupplierFormComponent } from './components/create-supplier-form-modal/create-supplier-form.component';
import { ImportContactsModalComponent } from './components/import-contacts-modal/import-contacts-modal.component';

export interface SupplierGroup {
    company: Company;
    users: User[];
}

@Component({
    selector: 'jft-suppliers',
    templateUrl: './suppliers.component.html',
    styleUrls: ['./suppliers.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SuppliersComponent implements OnInit {
    public supplier: Supplier;
    public suppliers: User[] = [];
    public filteredSuppliers: User[] = [];
    public availableTabs: UserTab[] = [];
    public suppliersArchived: User[] = [];
    public filteredSuppliersArchived: User[] = [];
    public groupedSuppliers: SupplierGroup[] = [];
    public groupedSuppliersArchived: SupplierGroup[] = [];
    public selectedTags: any[] = [];
    public tags: any[] = [];
    public searchTerm: string = '';

    // Contacts load in full but render a window of companies at a time; a
    // company's contacts always stay together, so the page unit is the company.
    public readonly pageSize = 25;
    public visibleCount = this.pageSize;
    public visibleCountArchived = this.pageSize;

    constructor(
        public user: User,
        private supplierService: SuppliersService,
        private route: ActivatedRoute,
        private router: Router,
        protected toast: ToastService,
        private injector: Injector,
        private ngbModal: NgbModal
    ) {
    }

    public ngOnInit() {
        let data = this.route.snapshot.data;
        this.suppliers = data['suppliers'];
        this.filteredSuppliers = [...this.suppliers];
        this.availableTabs = data['availableTabs'];
        this.refreshArchivedSuppliers();
        this.sortByCompanyName();
        this.initAvailableTabs();
        this.rebuildGroups();
    }

    public groupByCompany(users: User[]): SupplierGroup[] {
        const groups = new Map<number, SupplierGroup>();
        for (const user of users) {
            const id = Number(user.company.id);
            if (!groups.has(id)) {
                groups.set(id, { company: user.company, users: [] });
            }
            groups.get(id).users.push(user);
        }
        return Array.from(groups.values());
    }

    private rebuildGroups() {
        this.groupedSuppliers = this.groupByCompany(this.filteredSuppliers);
        this.groupedSuppliersArchived = this.groupByCompany(this.filteredSuppliersArchived);
        // Any change to the groups (search, tag filter, refresh) shows results
        // from the top again rather than preserving a now-stale window.
        this.visibleCount = this.pageSize;
        this.visibleCountArchived = this.pageSize;
    }

    public get visibleGroups(): SupplierGroup[] {
        return this.groupedSuppliers.slice(0, this.visibleCount);
    }

    public get visibleGroupsArchived(): SupplierGroup[] {
        return this.groupedSuppliersArchived.slice(0, this.visibleCountArchived);
    }

    public get hasMoreGroups(): boolean {
        return this.groupedSuppliers.length > this.visibleCount;
    }

    public get hasMoreGroupsArchived(): boolean {
        return this.groupedSuppliersArchived.length > this.visibleCountArchived;
    }

    public loadMore() {
        this.visibleCount += this.pageSize;
    }

    public loadMoreArchived() {
        this.visibleCountArchived += this.pageSize;
    }

    private initAvailableTabs() {
        this.availableTabs.forEach((c, i) => {
            this.tags.push({ id: c.id, name: c.name });
        });
    }

    public sortByCompanyName() {
        this.suppliers.sort((a, b) => a.company.name.localeCompare(b.company.name));
    }

    public createSupplier(): NgbModalRef {
        const modal: NgbModalRef = this.ngbModal.open(CreateSupplierFormComponent, {
            injector: Injector.create([
                // @ts-ignore
                { provide: Supplier, useValue: Supplier.create(this.supplier) },
            ], this.injector),
            size: 'lg',
            backdrop: 'static',
            centered: true
        })
        modal.result.then((data) => {
            this.refreshSuppliers();
        }, (error) => { });
        modal.componentInstance.formType = 'add';
        modal.componentInstance.availableTags = this.availableTabs;
        return modal;
    }

    public importContacts() {
        const modal = this.ngbModal.open(ImportContactsModalComponent, { size: 'lg', backdrop: 'static' });
        modal.result.then(
            (imported) => {
                if (imported) {
                    this.refreshSuppliers();
                }
            },
            () => { /* dismissed */ }
        );
    }

    public editSupplier(supplier: Company) {
        let supplierData = supplier['company']
        supplierData['contact'] = supplier;

        const modal: NgbModalRef = this.ngbModal.open(CreateSupplierFormComponent, {
            injector: Injector.create([
                { provide: Supplier, useValue: supplierData },
            ], this.injector),
            size: 'lg',
            backdrop: 'static',
            centered: true
        })
        modal.result.then((data) => {
            this.refreshSuppliers();
        }, (error) => { });
        modal.componentInstance.formType = 'edit';
        modal.componentInstance.availableTags = this.availableTabs;
    }

    public tabChange(event: NgbNavChangeEvent) {
        if (event.nextId === 'archived-suppliers') {
            this.refreshArchivedSuppliers();
            return
        }
        this.refreshSuppliers()
    }

    public onRefreshSuppliers() {
        this.refreshSuppliers();
    }

    public switchTab(tab) {
        this.selectedTags = [];
        if (tab == 'active') {
            this.refreshSuppliers();
        } else if (tab == 'archive') {
            this.refreshArchivedSuppliers();
        }
    }


    private refreshSuppliers(tags = []) {
        this.supplierService.fetchUsers(tags).subscribe((suppliers: User[]) => {
            this.suppliers = suppliers;
            this.applySearch();
        })
    }

    private refreshArchivedSuppliers(tags = []) {
        this.supplierService.fetchArchived(tags).subscribe((suppliers: User[]) => {
            this.suppliersArchived = suppliers;
            this.applySearchArchived();
        })
    }

    public archive(supplier: User) {
        this.confirmRemoveContact(supplier);
    }

    public confirmRemoveContact(supplier: User) {
        const fullName = `${supplier.firstName} ${supplier.lastName}`.trim();
        const modalRef = this.ngbModal.open(ConfirmModalComponent);
        modalRef.componentInstance.title = 'Remove Contact';
        modalRef.componentInstance.lead = 'Attention! This action can be undone from the Removed tab.';
        modalRef.componentInstance.text = `${fullName} will no longer appear in your contacts or in tender invitations. Their user account and any bids they have already submitted are unaffected.`;
        modalRef.componentInstance.details = [
            { label: 'Contact', value: fullName },
            { label: 'Email', value: supplier.email },
            { label: 'Company', value: supplier.company.name },
        ];
        modalRef.componentInstance.confirmButtonText = 'Remove Contact';
        modalRef.componentInstance.confirmButtonIcon = 'fas fa-user-minus';
        modalRef.result.then((confirmed) => {
            if (confirmed) {
                this.supplierService.archive(supplier).subscribe(() => {
                    this.toast.success(`${fullName} has been removed from your contacts`);
                    this.refreshSuppliers();
                    this.refreshArchivedSuppliers();
                });
            }
        }, () => {});
    }

    public unarchive(supplier: User) {
        const fullName = `${supplier.firstName} ${supplier.lastName}`.trim();
        const modalRef = this.ngbModal.open(ConfirmModalComponent);
        modalRef.componentInstance.title = 'Restore Contact';
        modalRef.componentInstance.lead = 'This contact will return to your active contacts.';
        modalRef.componentInstance.text = `${fullName} will appear again in your contacts and can be invited to tenders.`;
        modalRef.componentInstance.details = [
            { label: 'Contact', value: fullName },
            { label: 'Email', value: supplier.email },
            { label: 'Company', value: supplier.company.name },
        ];
        modalRef.componentInstance.confirmButtonText = 'Restore Contact';
        modalRef.componentInstance.confirmButtonClass = 'btn-success';
        modalRef.componentInstance.confirmButtonIcon = 'fas fa-rotate-left';
        modalRef.result.then((confirmed) => {
            if (confirmed) {
                this.supplierService.unarchive(supplier).subscribe(() => {
                    this.toast.success(`${fullName} has been restored to your contacts`);
                    this.refreshArchivedSuppliers();
                    this.refreshSuppliers();
                });
            }
        }, () => {});
    }

    public confirmRemoveCompany(group: SupplierGroup) {
        const count = group.users.length;
        const contacts = count === 1 ? '1 contact' : `${count} contacts`;
        const modalRef = this.ngbModal.open(ConfirmModalComponent);
        modalRef.componentInstance.title = 'Remove Company';
        modalRef.componentInstance.lead = 'Attention! This action can be undone from the Removed tab.';
        modalRef.componentInstance.text = `${group.company.name} and its ${contacts} will no longer appear in your contacts or in tender invitations. Their user accounts and any bids they have already submitted are unaffected. Other airlines are not affected.`;
        modalRef.componentInstance.details = [
            { label: 'Company', value: group.company.name },
            { label: 'Contacts', value: count },
        ];
        modalRef.componentInstance.confirmButtonText = 'Remove Company';
        modalRef.componentInstance.confirmButtonIcon = 'fas fa-building';
        modalRef.result.then((confirmed) => {
            if (confirmed) {
                this.supplierService.archiveCompany(group.company.id).subscribe(() => {
                    this.toast.success(`${group.company.name} has been removed from your contacts`);
                    this.refreshSuppliers();
                    this.refreshArchivedSuppliers();
                });
            }
        }, () => {});
    }

    public unarchiveCompany(group: SupplierGroup) {
        const count = group.users.length;
        const contacts = count === 1 ? '1 contact' : `${count} contacts`;
        const modalRef = this.ngbModal.open(ConfirmModalComponent);
        modalRef.componentInstance.title = 'Restore Company';
        modalRef.componentInstance.lead = 'This company will return to your active contacts.';
        modalRef.componentInstance.text = `${group.company.name} and its ${contacts} will appear again in your contacts and can be invited to tenders.`;
        modalRef.componentInstance.details = [
            { label: 'Company', value: group.company.name },
            { label: 'Contacts', value: count },
        ];
        modalRef.componentInstance.confirmButtonText = 'Restore Company';
        modalRef.componentInstance.confirmButtonClass = 'btn-success';
        modalRef.componentInstance.confirmButtonIcon = 'fas fa-rotate-left';
        modalRef.result.then((confirmed) => {
            if (confirmed) {
                this.supplierService.unarchiveCompany(group.company.id).subscribe(() => {
                    this.toast.success(`${group.company.name} has been restored to your contacts`);
                    this.refreshArchivedSuppliers();
                    this.refreshSuppliers();
                });
            }
        }, () => {});
    }

    public tagChange(tags) {
        this.refreshSuppliers(tags.map(item => item.id));
    }

    public urchivedTagChange(tags) {
        this.refreshArchivedSuppliers(tags.map(item => item.id));
    }

    public onSearchChange() {
        this.applySearch();
        this.applySearchArchived();
    }

    private applySearch() {
        if (!this.searchTerm.trim()) {
            this.filteredSuppliers = [...this.suppliers];
        } else {
            const term = this.searchTerm.toLowerCase().trim();
            this.filteredSuppliers = this.suppliers.filter(supplier =>
                supplier.company.name.toLowerCase().includes(term) ||
                supplier.firstName.toLowerCase().includes(term) ||
                supplier.lastName.toLowerCase().includes(term) ||
                supplier.email.toLowerCase().includes(term) ||
                (supplier.phone && supplier.phone.toLowerCase().includes(term)) ||
                (supplier.supplierAlias && supplier.supplierAlias.toLowerCase().includes(term))
            );
        }
        this.rebuildGroups();
    }

    private applySearchArchived() {
        if (!this.searchTerm.trim()) {
            this.filteredSuppliersArchived = [...this.suppliersArchived];
        } else {
            const term = this.searchTerm.toLowerCase().trim();
            this.filteredSuppliersArchived = this.suppliersArchived.filter(supplier =>
                supplier.company.name.toLowerCase().includes(term) ||
                supplier.firstName.toLowerCase().includes(term) ||
                supplier.lastName.toLowerCase().includes(term) ||
                supplier.email.toLowerCase().includes(term) ||
                (supplier.phone && supplier.phone.toLowerCase().includes(term)) ||
                (supplier.supplierAlias && supplier.supplierAlias.toLowerCase().includes(term))
            );
        }
        this.rebuildGroups();
    }

    public getActiveSupplierCount(): number {
        return this.filteredSuppliers.length;
    }

    public getArchivedSupplierCount(): number {
        return this.filteredSuppliersArchived.length;
    }

    public getTotalSupplierCount(): number {
        return this.getActiveSupplierCount() + this.getArchivedSupplierCount();
    }

    public getUniqueCompanyCount(): number {
        const activeIds = this.suppliers.map(u => u.company.id);
        const archivedIds = this.suppliersArchived.map(u => u.company.id);
        const allIds = new Set([...activeIds, ...archivedIds]);
        return allIds.size;
    }

    public getTotalUserCount(): number {
        return this.suppliers.length + this.suppliersArchived.length;
    }

    public downloadSupplierContacts() {
        this.supplierService.downloadSupplierContacts();
    }

    public clearSearch() {
        this.searchTerm = '';
        this.onSearchChange();
    }
}
